<template>
  <div
    v-if="visible"
    class="fixed z-[100009] rounded-xl select-none"
    :class="[
      isDragging ? 'cursor-grabbing' : (draggable ? 'cursor-grab' : ''),
      isDragging ? '' : 'transition-all duration-300 ease-in-out'
    ]"
    :style="buttonStyle"
    @mousedown="handleMouseDown"
    @touchstart="handleTouchStart"
  >
    <div
      class="bg-gradient-to-br from-purple-600 to-indigo-600 backdrop-blur-xl rounded-xl p-3 shadow-lg shadow-purple-500/30 border border-white/20 min-w-[240px] transition-all relative"
      :class="{'scale-95': isDragging,'hover:shadow-xl hover:shadow-purple-500/40': !isDragging}"
    >
      <!-- 关闭按钮 -->
      <button
        class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 active:scale-95 z-10"
        @click.stop="handleClose"
        title="关闭"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
      
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">设备类型:</span>
          <span class="text-white font-bold px-2 py-0.5 rounded-md backdrop-blur-sm font-mono" :class="deviceTypeClass">
            {{ deviceTypeText }}
          </span>
        </div>
        <div class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">分辨率:</span>
          <span class="text-white font-bold px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm font-mono">
            {{ deviceWidth }} × {{ deviceHeight }}
          </span>
        </div>
        <div class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">触摸屏:</span>
          <span class="text-white font-bold px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm font-mono">
            {{ hasTouch ? '是' : '否' }}
          </span>
        </div>
        <div class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">浏览器:</span>
          <span class="text-white font-bold px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm font-mono text-[10px]">
            {{ browserInfo }}
          </span>
        </div>
        <div class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">操作系统:</span>
          <span class="text-white font-bold px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm font-mono text-[10px]">
            {{ osInfo }}
          </span>
        </div>
        <div v-if="deviceModel" class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">设备型号:</span>
          <span class="text-white font-bold px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm font-mono text-[10px]">
            {{ deviceModel }}
          </span>
        </div>
        <div class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">像素比:</span>
          <span class="text-white font-bold px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm font-mono">
            {{ pixelRatio }}x
          </span>
        </div>
        <div class="flex items-center justify-between text-xs leading-relaxed">
          <span class="text-white/85 font-medium">User Agent:</span>
          <button
            class="text-white font-bold px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm hover:bg-white/25 transition-colors cursor-pointer"
            @click.stop="showUAModal = true"
          >
            查看
          </button>
        </div>
      </div>
    </div>

    <!-- UA 弹窗 -->
    <a-modal
      v-model:open="showUAModal"
      title="User Agent 信息"
      :footer="null"
      :width="700"
      :z-index="1000002"
      @cancel="showUAModal = false"
    >
      <div class="bg-gray-50 p-4 rounded-lg">
        <p class="text-sm text-gray-700 break-all leading-relaxed font-mono">
          {{ userAgent }}
        </p>
      </div>
      <div class="mt-4 flex justify-end">
        <a-button type="primary" @click="copyUA">
          复制 UA
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { getDeviceType, getViewportWidth, getViewportHeight } from '@/utils/viewport'

export default {
  name: 'DeviceInfo',
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    initialRight: {
      type: Number,
      default: 12
    },
    initialTop: {
      type: Number,
      default: 20
    },
    draggable: {
      type: Boolean,
      default: true
    },
    // 限制拖拽的容器选择器或元素（如果不设置则使用整个窗口）
    container: {
      type: [String, HTMLElement],
      default: 'main'
    }
  },
  data () {
    return {
      deviceWidth: 0,
      deviceHeight: 0,
      hasTouch: false,
      browserInfo: '',
      osInfo: '',
      deviceModel: '',
      pixelRatio: 1,
      userAgent: '',
      showUAModal: false,
      isDragging: false,
      currentX: 0,
      currentY: 0,
      initialX: 0,
      initialY: 0,
      xOffset: 0,
      yOffset: 0
    }
  },
  computed: {
    buttonStyle () {
      // 使用 right/top 定位替代 transform，避免创建层叠上下文导致 z-index 失效
      // 这样可以确保 z-[100009] 在所有浏览器中正常工作
      return {
        right: `${this.initialRight - this.xOffset}px`,
        top: `${this.initialTop + this.yOffset}px`
      }
    },
    deviceType () {
      return getDeviceType()
    },
    deviceTypeText () {
      const typeMap = {
        mobile: '📱 移动端',
        tablet: '📱 平板',
        desktop: '💻 桌面端'
      }
      return typeMap[this.deviceType] || '未知'
    },
    deviceTypeClass () {
      const classMap = {
        mobile: 'bg-green-500/25 text-green-200',
        tablet: 'bg-blue-500/25 text-blue-200',
        desktop: 'bg-purple-500/25 text-purple-200'
      }
      return classMap[this.deviceType] || 'bg-white/15'
    }
  },
  mounted () {
    this.updateDeviceInfo()
    
    // 创建防抖的更新函数
    this._debouncedUpdate = this.debounce(this.updateDeviceInfo.bind(this), 200)
    
    window.addEventListener('resize', this._debouncedUpdate, { passive: true })
    window.addEventListener('orientationchange', this._debouncedUpdate)
  },
  beforeUnmount () {
    if (this._debouncedUpdate) {
      window.removeEventListener('resize', this._debouncedUpdate)
      window.removeEventListener('orientationchange', this._debouncedUpdate)
    }
    this.cleanupDragListeners()
  },
  methods: {
    handleClose () {
      this.$emit('close')
    },
    // 防抖函数
    debounce(fn, delay) {
      let timer = null
      return function(...args) {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
          fn.apply(this, args)
        }, delay)
      }
    },
    updateDeviceInfo () {
      // 强制刷新 viewport.js 的缓存
      const { refreshViewport } = require('@/utils/viewport')
      refreshViewport()
      
      // 然后获取最新值
      this.deviceWidth = getViewportWidth()
      this.deviceHeight = getViewportHeight()
      this.hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      this.browserInfo = this.getBrowserInfo()
      this.osInfo = this.getOSInfo()
      this.deviceModel = this.getDeviceModel()
      this.pixelRatio = window.devicePixelRatio || 1
      this.userAgent = navigator.userAgent
      
      // console.log('🔄 [DeviceInfo] 设备信息已更新:', {
      //   deviceType: this.deviceType,
      //   deviceWidth: this.deviceWidth,
      //   deviceHeight: this.deviceHeight,
      //   hasTouch: this.hasTouch
      // })
    },
    getBrowserInfo () {
      const ua = navigator.userAgent
      let browser = '未知'
      let version = ''

      if (ua.indexOf('Edg') > -1) {
        browser = 'Edge'
        version = ua.match(/Edg\/(\d+)/)?.[1] || ''
      } else if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
        browser = 'Chrome'
        version = ua.match(/Chrome\/(\d+)/)?.[1] || ''
      } else if (ua.indexOf('Firefox') > -1) {
        browser = 'Firefox'
        version = ua.match(/Firefox\/(\d+)/)?.[1] || ''
      } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
        browser = 'Safari'
        version = ua.match(/Version\/(\d+)/)?.[1] || ''
      } else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) {
        browser = 'IE'
        version = ua.match(/(?:MSIE |rv:)(\d+)/)?.[1] || ''
      }

      return version ? `${browser} ${version}` : browser
    },
    getOSInfo () {
      const ua = navigator.userAgent
      const platform = navigator.platform

      if (ua.indexOf('Win') > -1) {
        if (ua.indexOf('Windows NT 10.0') > -1) return 'Windows 10/11'
        if (ua.indexOf('Windows NT 6.3') > -1) return 'Windows 8.1'
        if (ua.indexOf('Windows NT 6.2') > -1) return 'Windows 8'
        if (ua.indexOf('Windows NT 6.1') > -1) return 'Windows 7'
        return 'Windows'
      } else if (ua.indexOf('Mac') > -1) {
        const match = ua.match(/Mac OS X (\d+)[._](\d+)/)
        if (match) {
          return `macOS ${match[1]}.${match[2]}`
        }
        return 'macOS'
      } else if (ua.indexOf('Android') > -1) {
        const match = ua.match(/Android (\d+\.?\d*)/)
        return match ? `Android ${match[1]}` : 'Android'
      } else if (ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
        const match = ua.match(/OS (\d+)[._](\d+)/)
        if (match) {
          return `iOS ${match[1]}.${match[2]}`
        }
        return 'iOS'
      } else if (ua.indexOf('Linux') > -1) {
        return 'Linux'
      }

      return platform || '未知'
    },
    getDeviceModel () {
      const ua = navigator.userAgent

      // 尝试获取移动设备型号
      if (ua.indexOf('iPhone') > -1) {
        return 'iPhone'
      } else if (ua.indexOf('iPad') > -1) {
        return 'iPad'
      } else if (ua.indexOf('Android') > -1) {
        // 尝试提取 Android 设备型号
        const match = ua.match(/\(([^)]+)\)/)
        if (match && match[1]) {
          const parts = match[1].split(';')
          for (let part of parts) {
            part = part.trim()
            if (part && !part.includes('Android') && !part.includes('Build') && 
                !part.includes('Linux') && !part.includes('Mobile') && 
                part.length > 2 && part.length < 30) {
              return part
            }
          }
        }
      }

      return ''
    },
    copyUA () {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.userAgent).then(() => {
          this.$message.success('UA 已复制到剪贴板')
        }).catch(() => {
          this.fallbackCopyUA()
        })
      } else {
        this.fallbackCopyUA()
      }
    },
    fallbackCopyUA () {
      const textarea = document.createElement('textarea')
      textarea.value = this.userAgent
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.$message.success('UA 已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      }
      document.body.removeChild(textarea)
    },
    handleResize () {
      this.updateDeviceInfo()
      // 窗口大小改变时，重新约束位置
      if (this.xOffset !== 0 || this.yOffset !== 0) {
        const constrained = this.constrainPosition(this.xOffset, this.yOffset)
        this.xOffset = constrained.x
        this.yOffset = constrained.y
      }
    },
    // 获取容器边界
    getContainerBounds () {
      const { getViewportWidth, getViewportHeight } = require('@/utils/viewport')
      if (!this.container) {
        return {
          width: getViewportWidth(),
          height: getViewportHeight(),
          top: 0,
          left: 0
        }
      }

      let containerEl = this.container
      if (typeof this.container === 'string') {
        containerEl = document.querySelector(this.container)
      }

      if (!containerEl) {
        return {
          width: getViewportWidth(),
          height: getViewportHeight(),
          top: 0,
          left: 0
        }
      }

      const rect = containerEl.getBoundingClientRect()
      return {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left
      }
    },
    // 限制拖拽边界，确保面板不超出容器区域
    constrainPosition (x, y) {
      const { getViewportWidth, getViewportHeight } = require('@/utils/viewport')
      const panelWidth = 220
      const panelHeight = 120
      const bounds = this.getContainerBounds()

      // 如果有容器限制，需要考虑容器的位置
      if (this.container) {
        // 计算面板相对于窗口的位置
        const panelRight = getViewportWidth() - this.initialRight
        const panelTop = this.initialTop

        // 容器的边界（相对于窗口）
        const containerLeft = bounds.left
        const containerRight = bounds.left + bounds.width
        const containerTop = bounds.top
        const containerBottom = bounds.top + bounds.height

        // 面板当前的左边缘和上边缘位置
        const currentLeft = panelRight - panelWidth + x
        const currentTop = panelTop + y

        // 限制 x 偏移量
        let constrainedX = x
        if (currentLeft < containerLeft) {
          constrainedX = x + (containerLeft - currentLeft)
        } else if (currentLeft + panelWidth > containerRight) {
          constrainedX = x - (currentLeft + panelWidth - containerRight)
        }

        // 限制 y 偏移量
        let constrainedY = y
        if (currentTop < containerTop) {
          constrainedY = y + (containerTop - currentTop)
        } else if (currentTop + panelHeight > containerBottom) {
          constrainedY = y - (currentTop + panelHeight - containerBottom)
        }

        return { x: constrainedX, y: constrainedY }
      } else {
        // 窗口边界限制逻辑
        const viewportWidth = getViewportWidth()
        const viewportHeight = getViewportHeight()
        const maxX = viewportWidth - this.initialRight - panelWidth
        const minX = -this.initialRight
        const maxY = viewportHeight - this.initialTop - panelHeight
        const minY = -this.initialTop
        const constrainedX = Math.max(minX, Math.min(maxX, x))
        const constrainedY = Math.max(minY, Math.min(maxY, y))
        return { x: constrainedX, y: constrainedY }
      }
    },
    handleMouseDown (e) {
      if (!this.draggable) return

      this.initialX = e.clientX - this.xOffset
      this.initialY = e.clientY - this.yOffset

      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.handleMouseUp)
    },
    handleMouseMove (e) {
      if (!this.draggable) return

      e.preventDefault()
      this.isDragging = true

      this.currentX = e.clientX - this.initialX
      this.currentY = e.clientY - this.initialY

      const constrained = this.constrainPosition(this.currentX, this.currentY)
      this.xOffset = constrained.x
      this.yOffset = constrained.y
    },
    handleMouseUp () {
      if (!this.draggable) return

      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)

      setTimeout(() => {
        this.isDragging = false
      }, 100)
    },
    handleTouchStart (e) {
      if (!this.draggable) return

      const touch = e.touches[0]
      this.initialX = touch.clientX - this.xOffset
      this.initialY = touch.clientY - this.yOffset

      document.addEventListener('touchmove', this.handleTouchMove, { passive: false })
      document.addEventListener('touchend', this.handleTouchEnd)
    },
    handleTouchMove (e) {
      if (!this.draggable) return

      e.preventDefault()
      this.isDragging = true

      const touch = e.touches[0]
      this.currentX = touch.clientX - this.initialX
      this.currentY = touch.clientY - this.initialY

      const constrained = this.constrainPosition(this.currentX, this.currentY)
      this.xOffset = constrained.x
      this.yOffset = constrained.y
    },
    handleTouchEnd () {
      if (!this.draggable) return

      document.removeEventListener('touchmove', this.handleTouchMove)
      document.removeEventListener('touchend', this.handleTouchEnd)

      setTimeout(() => {
        this.isDragging = false
      }, 100)
    },
    cleanupDragListeners () {
      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)
      document.removeEventListener('touchmove', this.handleTouchMove)
      document.removeEventListener('touchend', this.handleTouchEnd)
    }
  }
}
</script>
<style scoped>
/* 防止拖拽时选中文本 */
.cursor-grab,
.cursor-grabbing {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
.cursor-grab {
  cursor: grab;
  cursor: -webkit-grab;
}
.cursor-grabbing {
  cursor: grabbing !important;
  cursor: -webkit-grabbing !important;
}
</style>